import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, children, className = '' }: FieldProps) {
  return (
    <label className={['flex flex-col gap-2', className].join(' ')}>
      {label && <span className="text-sm font-medium text-fg-2">{label}</span>}
      {children}
      {hint && !error && <span className="text-xs text-muted leading-snug">{hint}</span>}
      {error && <span className="text-xs text-danger leading-snug">{error}</span>}
    </label>
  );
}

const inputBase =
  'block w-full bg-card border rounded-lg px-4 py-3 text-[15px] text-fg placeholder:text-faint outline-none transition focus:border-fg focus:ring-2 focus:ring-fg/10 disabled:opacity-50';

export function Input(props: InputHTMLAttributes<HTMLInputElement> & { error?: boolean }) {
  const { error, className = '', ...rest } = props;
  return (
    <input
      {...rest}
      className={[
        inputBase,
        error ? 'border-danger focus:border-danger focus:ring-danger/15' : 'border-line',
        className,
      ].join(' ')}
    />
  );
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean }) {
  const { error, className = '', ...rest } = props;
  return (
    <textarea
      {...rest}
      className={[
        inputBase,
        'min-h-[120px] resize-y leading-relaxed',
        error ? 'border-danger focus:border-danger focus:ring-danger/15' : 'border-line',
        className,
      ].join(' ')}
    />
  );
}

export function Checkbox(
  props: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode },
) {
  const { label, className = '', ...rest } = props;
  return (
    <label className={['flex items-start gap-3 text-sm text-fg-2 leading-snug cursor-pointer', className].join(' ')}>
      <input
        {...rest}
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-fg cursor-pointer"
      />
      <span>{label}</span>
    </label>
  );
}
